import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import FileImport from '../../assets/FileImport.svg?react';
import SetupBubbles2 from '../../assets/SetupBubbles2.png';
import SetupProjectIcon from '../../assets/SetupProjectIcon.svg?react';
import { PLAN_FILE_ACCEPT } from './planFiles';

interface CadSetupPanelProps {
  /** The chosen file (drawing or image); the editor reads it. */
  onFileSelected: (file: File) => void;
  /** A drawing is being read (DWG conversion, parsing). */
  importing: boolean;
  error: string | null;
  onSkip: () => void;
  /** The drawing being started (named when it was created): its title and how to leave. */
  drawing: { title: string; onCancel: () => void };
}

export function CadSetupPanel({ onFileSelected, importing, error, onSkip, drawing }: CadSetupPanelProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFile = useCallback(
    (file: File) => {
      if (!importing) onFileSelected(file);
    },
    [importing, onFileSelected],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFile(file);
      e.target.value = '';
    },
    [handleFile],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [handleFile],
  );

  return (
    <div
      className="fixed top-[64px] bottom-0 right-0 h-[calc(100vh-64px)] w-full md:w-[455px] md:rounded-l-2xl text-gray-300 flex flex-col overflow-y-auto bg-black shadow-2xl pointer-events-auto"
      style={{ zIndex: 100 }}
    >
      <div className="flex-shrink-0 bg-[#232323] pt-4 md:pt-0">
        <div className="h-[55px] flex items-center px-6 gap-3 relative overflow-hidden">
          <div className="p-2 rounded-[10px] bg-[#313131] flex-shrink-0">
            <SetupProjectIcon className="w-4 h-4 text-lime-400" strokeWidth={2} />
          </div>
          <h1 className="text-xl md:text-2xl font-bold text-white truncate">{drawing.title}</h1>
          <img
            src={SetupBubbles2}
            alt=""
            className="absolute -top-[320px] -right-[200px] rotate-90 pointer-events-none select-none hidden md:block"
            style={{
              WebkitMaskImage: 'linear-gradient(to top, transparent 0%, black 60%)',
              maskImage: 'linear-gradient(to top, transparent 0%, black 60%)',
            }}
          />
        </div>
      </div>

      <div className="bg-[#232323] overflow-y-auto flex-1">
        <div className="pb-8">
          <div className="mt-4 px-6">
            <input
              type="file"
              accept={PLAN_FILE_ACCEPT}
              ref={fileInputRef}
              onChange={handleInputChange}
              className="hidden"
            />

            <label
              onClick={() => fileInputRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                setIsDragging(false);
              }}
              className={`block border-2 border-dashed rounded-xl flex flex-col items-center justify-center gap-3 cursor-pointer transition h-[200px]
                ${isDragging ? 'border-lime-400 bg-lime-400/5' : 'border-black hover:border-lime-400/50'}
              `}
            >
              <FileImport className="w-10 h-10 text-lime-400" />
              <div className="text-center">
                <p className="text-sm text-white font-medium">
                  {importing ? t('projects.cadProject.importing') : t('projects.cadProject.uploadHint')}
                </p>
                <p className="text-xs text-gray-400 mt-1">{t('projects.cadProject.supportedFiles')}</p>
              </div>
            </label>

            {error && <p className="text-xs text-red-400 mt-2">{error}</p>}
          </div>

          {/* Draw manually — OR separator */}
          <div className="mt-6 px-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 h-[1px] bg-white/10" />
              <span className="text-[11px] text-gray-500 uppercase tracking-widest">
                {t('projects.cadProject.or', 'OR')}
              </span>
              <div className="flex-1 h-[1px] bg-white/10" />
            </div>
            <p className="text-xs text-gray-400 mb-3 text-center">
              {t('projects.cadProject.drawManuallyHint', 'No file? Draw your floor plan directly on a blank canvas.')}
            </p>
            <div className="flex justify-center">
              <button
                onClick={onSkip}
                className="flex items-center rounded-full font-medium transition-colors duration-200 text-xs bg-lime-400 text-black hover:bg-lime-500"
                style={{ height: '36px', paddingLeft: '18px', paddingRight: '18px', gap: '8px' }}
              >
                {t('projects.cadProject.drawManually', 'Draw manually')} →
              </button>
            </div>
          </div>

          <div className="mt-8 px-6">
            <div className="bg-[#313131] rounded-xl p-5">
              <h3 className="text-sm font-medium text-white mb-3">{t('projects.cadProject.howItWorks')}</h3>
              <div className="space-y-3 text-xs text-gray-400">
                <div className="flex gap-3">
                  <span className="w-5 h-5 rounded-full bg-lime-400/20 text-lime-400 flex items-center justify-center text-[10px] font-bold shrink-0">
                    1
                  </span>
                  <span>{t('projects.cadProject.step1')}</span>
                </div>
                <div className="flex gap-3">
                  <span className="w-5 h-5 rounded-full bg-lime-400/20 text-lime-400 flex items-center justify-center text-[10px] font-bold shrink-0">
                    2
                  </span>
                  <span>{t('projects.cadProject.step2')}</span>
                </div>
                <div className="flex gap-3">
                  <span className="w-5 h-5 rounded-full bg-lime-400/20 text-lime-400 flex items-center justify-center text-[10px] font-bold shrink-0">
                    3
                  </span>
                  <span>{t('projects.cadProject.step3')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-bl-2xl bg-[#232323] w-full h-[60px] flex px-6 items-center border-t border-gray-800">
        <button
          onClick={drawing.onCancel}
          className="flex items-center rounded-full font-medium transition-colors duration-200 text-xs border border-[#B3EC49] text-[#B3EC49] hover:border-lime-500"
          style={{
            height: '36px',
            paddingTop: '10px',
            paddingBottom: '10px',
            paddingLeft: '18px',
            paddingRight: '18px',
            gap: '8px',
          }}
        >
          {t('common.cancel')}
        </button>
      </div>
    </div>
  );
}
